/**
 * useAccountMutations — B10.2 + B10.6
 *
 * PATCH /api/v2/account/me — Settings aktualisieren.
 * PATCH /api/v2/account/me/password — Passwort aendern.
 *
 * AUTH-Block-Fix: Contract-Mismatch behoben. Die beiden Endpoints liefern bei
 * 422 UNTERSCHIEDLICHE Fehlerformen:
 *   - PATCH .../me/password -> { error: { code, message, details } } (verschachtelt)
 *   - PATCH .../me          -> { errors: string[] } (flache Meldungsliste, kein Feldbezug)
 * Vormals wurde fuer beide result.error.details gelesen (existiert bei keinem
 * der beiden Formate) — Feldfehler kamen dadurch nie im FE an.
 */
import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import apiClient from '../../lib/api-client'
import { ACCOUNT_QUERY_KEY, type AccountUpdatePayload } from './useAccount'

export type PasswordChangePayload = {
  current_password: string
  new_password: string
  new_password_confirmation: string
}

export type ApiFieldErrors = Record<string, string>

export type MutationResult =
  | { ok: true }
  | { ok: false; fieldErrors?: ApiFieldErrors; message?: string }

export function useAccountMutations() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = apiClient as any
  const queryClient = useQueryClient()
  const { t } = useTranslation('settings')
  const [isMutating, setIsMutating] = useState(false)

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY })
  }, [queryClient])

  const updateSettings = useCallback(
    async (payload: AccountUpdatePayload): Promise<MutationResult> => {
      setIsMutating(true)
      try {
        const result = await client.PATCH('/api/v2/account/me', { body: payload })
        if (result.error) {
          // Contract: { errors: string[] } — flache Meldungsliste ohne Feldbezug
          // (kein details-Objekt wie bei /me/password, siehe Kommentar oben).
          const err = result.error as { errors?: string[] }
          const message =
            Array.isArray(err.errors) && err.errors.length > 0
              ? err.errors.join(', ')
              : t('mutations.saveFailed')
          return { ok: false, message }
        }
        invalidate()
        return { ok: true }
      } catch {
        return { ok: false, message: t('mutations.saveNetworkError') }
      } finally {
        setIsMutating(false)
      }
    },
    [client, invalidate, t],
  )

  const changePassword = useCallback(
    async (payload: PasswordChangePayload): Promise<MutationResult> => {
      setIsMutating(true)
      try {
        const result = await client.PATCH('/api/v2/account/me/password', { body: payload })
        if (result.error) {
          // Contract: { error: { code, message, details } } — verschachtelt
          // unter `error` (siehe Kommentar oben, live verifiziert).
          const err = result.error as {
            error?: { code?: string; message?: string; details?: ApiFieldErrors }
          }
          return {
            ok: false,
            fieldErrors: err.error?.details,
            message: err.error?.message ?? t('mutations.passwordChangeFailed'),
          }
        }
        return { ok: true }
      } catch {
        return { ok: false, message: t('mutations.passwordChangeNetworkError') }
      } finally {
        setIsMutating(false)
      }
    },
    [client, t],
  )

  return {
    isMutating,
    updateSettings,
    changePassword,
  }
}
